/** @jsxImportSource @emotion/react */
import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { useState, useMemo, useEffect } from 'react'
import { Box, useTheme, Typography, Pagination } from '@mui/material'

import { toast } from '@app/components/ui/Toast/toast'
import { AdaptationLibraryCard } from '../components/AdaptationLibrary.card'
import {
  useFetchAdaptationFilters,
  useFetchAdaptationLibrary,
} from '../hooks/useFetchAdaptationLibrary'
import { LoadingSpinner } from '@app/components/ui/LoadingSpinner/LoadingSpinner'
import { AdaptationLibraryFilters } from '../components/AdaptationLibrary.filters'

import { appLink } from '@app/config/appLink'
import { clientNameAtom } from '@app/state/clientsAtom'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import { INITIAL_FILTER_STATE, PAGE_SIZE } from '../utils/adaptationLibrary.constants'
import { AdaptationLibraryItem, AdaptationLibraryFilter } from '../utils/adaptationLibrary.types'
import {
  paginationWrapper,
  adaptationLibraryWrapper,
  adaptationLibraryMainTitle,
  adaptationLibraryPageWrapper,
} from '../styles/AdaptationLibrary.styles'
import { FeatureBanner } from '@app/components/Atoms/FeatureBanner/FeatureBanner'

export const AdaptationLibraryPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const currencySymbol = useAtomValue(currencySymbolAtom)
  const clientName = useAtomValue(clientNameAtom)

  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState<AdaptationLibraryFilter>(INITIAL_FILTER_STATE)

  const formattedCostsForApi = useMemo(() => {
    return filter.cost.map((cost) =>
      cost
        .replaceAll(currencySymbol, '$')
        .replace('Low', '')
        .replace('Medium', '')
        .replace('High', '')
        .trim(),
    )
  }, [filter, currencySymbol])

  const { data, isLoading, isError } = useFetchAdaptationLibrary(
    clientName ?? '',
    page,
    PAGE_SIZE,
    { ...filter, cost: formattedCostsForApi },
  )

  const {
    data: filterData,
    isLoading: filterLoading,
    isError: filterError,
  } = useFetchAdaptationFilters(clientName ?? '')

  useEffect(() => {
    if (isError || filterError) {
      toast({
        type: 'error',
        position: 'top-center',
        description: 'Something went wrong loading the Adaptation Library, Please try again',
      })
      navigate(appLink.homepage.path)
    }
  }, [isError, filterError, navigate])

  if (isError || filterError) return <p>Something went wrong</p>

  return (
    <>
      <FeatureBanner featureName="adaptationLibrary" />
      <Box css={adaptationLibraryPageWrapper({ theme })}>
        <Typography css={adaptationLibraryMainTitle()}>Adaptation Library</Typography>
        <>
          {filterLoading || !filterData ? (
            'loading filters'
          ) : (
            <AdaptationLibraryFilters
              filters={filter}
              setFilters={setFilter}
              uniqueFilterItems={filterData || INITIAL_FILTER_STATE}
            />
          )}
        </>
        {isLoading || !data ? (
          <LoadingSpinner />
        ) : (
          <>
            <p style={{ marginTop: 24, fontSize: 13 }}>
              Showing {data.adaptation_interventions.length} of {data.total_count} interventions
            </p>
            <Box css={adaptationLibraryWrapper({ theme })}>
              {data.adaptation_interventions.map((adaptationLibraryItem: AdaptationLibraryItem) => {
                return (
                  <AdaptationLibraryCard
                    key={adaptationLibraryItem.intervention_name}
                    adaptationLibraryItem={adaptationLibraryItem}
                  />
                )
              })}
            </Box>
            <Box css={paginationWrapper()}>
              <Pagination
                page={page}
                onChange={(_, value) => setPage(value)}
                count={data.page_count}
              />
            </Box>
          </>
        )}
      </Box>
    </>
  )
}
